import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { RootState } from '../../../redux/reducers';
import { formatDate } from '../../../utils/stringUtils';
import {
  BankTransactionParticipation,
  CreateBankTransactionModel,
  TransactionType,
  BankTransactionWithParticipation,
} from '../../../types/bankAccount';
import Button from '../buttons/Button';
import { ConnectionType } from '../../../redux/slices/connectionsSlice';
import ModalLayout from '../layouts/ModalLayout';
import Labeled from '../misc/Labeled';
import DropdownSelect from '../forms/DropdownSelect';
import CurrencyInput from '../forms/CurrencyInput';

import SplitMembersList from '../financial/SplitMembersList';
import DatePicker from '../forms/DatePicker';
import Slider from '../misc/Slider';

// Helper function to convert BankTransactionWithParticipation to CreateBankTransactionModel
function convertToCreateModel(
  transaction: BankTransactionWithParticipation,
): CreateBankTransactionModel {
  return {
    amount: transaction.amount,
    description: transaction.description,
    date: transaction.date ? new Date(transaction.date) : new Date(),
    mutationId: transaction.mutationId,
    payerId: transaction.payerId,
    transactionType: transaction.transactionType,
    participations: transaction.participations || undefined,
    contributionPayment: transaction.contributionPayment,
  };
}

interface DeclarationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialTransaction?: CreateBankTransactionModel | BankTransactionWithParticipation;
  onSplitSuccess?: (transaction: CreateBankTransactionModel) => void;
}

type DeclarationState = {
  description: string;
  date: Date;
  amount: number;
  transactionType?: TransactionType;
  payerId?: number;
  mutationId?: number;
  selectedMembers: number[];
  memberValues: Record<number, number>; // store absolute values in cents
  memberRations?: Record<number, number | undefined>;
  contributionPayment?: {
    contributionPlanId?: number;
    dashboardMemberId?: number;
  };
  isContributionPayment?: boolean;
};

const EMPTY_DECLARATION_STATE = {
  description: '',
  date: new Date(),
  amount: 0,
  selectedMembers: [],
  memberValues: {},
  memberRations: {},
};

const transformTransactionModelIntoState = (tns: CreateBankTransactionModel) => {
  return {
    ...tns,
    date: tns.date ? new Date(tns.date) : new Date(),
    contributionPayment: tns.contributionPayment ?? undefined,
    selectedMembers: tns.participations?.map((p) => p.dashboardMemberId) ?? [],
    memberValues:
      tns.participations?.reduce(
        (acc, p) => {
          acc[p.dashboardMemberId] = p.amount;
          return acc;
        },
        {} as Record<number, number>,
      ) ?? {},
  };
};

export default function DeclarationModal({
  open,
  setOpen,
  onSplitSuccess,
  initialTransaction,
}: DeclarationModalProps) {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.splitModal' });
  const { t: tEnum } = useTranslation('', { keyPrefix: 'page.financial.enums' });
  const connectionsAll = useSelector((state: RootState) => state.connections);
  const connections = connectionsAll.filter((c) => c.type === ConnectionType.MEMBER || c.type === ConnectionType.EXTERNAL_MEMBER);
  const [declaration, setDeclaration] = useState<DeclarationState>({
    ...EMPTY_DECLARATION_STATE,
  });
  const [remainingAmount, setRemainingAmount] = useState(initialTransaction?.amount ?? 0);

  const editMode = useMemo(() => !!initialTransaction, [initialTransaction]);

  useEffect(() => {
    if (!initialTransaction) {
      setDeclaration({ ...EMPTY_DECLARATION_STATE });
      setRemainingAmount(0);
    } else {
      const transactionToUse =
        'transactionId' in initialTransaction
          ? convertToCreateModel(initialTransaction)
          : initialTransaction;
      setDeclaration(transformTransactionModelIntoState(transactionToUse));
      setRemainingAmount(initialTransaction.amount);
    }
  }, [initialTransaction]);

  useEffect(() => {
    if (
      declaration.transactionType &&
      (declaration.transactionType.startsWith('GROUP') ||
        declaration.transactionType === TransactionType.CONTRIBUTION)
    ) {
      setDeclaration((prevTns) => ({
        ...prevTns,
        selectedMembers: [],
        memberValues: {},
        memberRations: {},
      }));
      setRemainingAmount(0);
    }
  }, [declaration.transactionType]);

  // Rations-based recalculation similar to AssignSplitTransactionModal
  const recalcFromRations = (
    prev: DeclarationState,
    override?: {
      memberValues?: Record<number, number>;
      memberRations?: Record<number, number | undefined>;
    },
  ): DeclarationState => {
    const totalAbs = Math.abs(prev.amount);
    const memberValues = override?.memberValues ?? prev.memberValues ?? {};
    const memberRations = override?.memberRations ?? prev.memberRations ?? {};

    const manualIds = Object.keys(memberValues)
      .map((id) => +id)
      .filter((id) => memberRations[id] === undefined);
    const manualAbsSum = manualIds.reduce((sum, id) => sum + Math.abs(memberValues[id] || 0), 0);
    const remainingAbs = Math.max(0, totalAbs - manualAbsSum);

    const rationEntries = Object.entries(memberRations)
      .map(([k, v]) => ({ id: +k, r: v }))
      .filter((e) => e.r !== undefined);
    const sumRations = rationEntries.reduce((sum, e) => sum + (e.r || 0), 0);

    const newMemberValues: Record<number, number> = { ...memberValues };
    if (sumRations > 0 && remainingAbs > 0) {
      let allocatedAbs = 0;
      rationEntries.forEach((e) => {
        const r = e.r || 0;
        if (r <= 0) {
          newMemberValues[e.id] = 0;
        } else {
          const shareAbs = Math.floor((remainingAbs * r) / sumRations);
          newMemberValues[e.id] = shareAbs;
          allocatedAbs += shareAbs;
        }
      });
      let remainder = remainingAbs - allocatedAbs;
      rationEntries.some((e) => {
        if (remainder <= 0) return true;
        if ((e.r || 0) > 0) {
          newMemberValues[e.id] = (newMemberValues[e.id] || 0) + 1;
          remainder -= 1;
        }
        return false;
      });
    } else {
      rationEntries.forEach((e) => {
        if ((e.r || 0) === 0) newMemberValues[e.id] = 0;
      });
    }

    return { ...prev, memberValues: newMemberValues, memberRations };
  };

  useEffect(() => {
    if (declaration.transactionType && declaration.transactionType.startsWith('PERSONAL')) {
      const totalAssignedAbs = Object.values(declaration.memberValues).reduce(
        (sum, value) => sum + Math.abs(value || 0),
        0,
      );
      setRemainingAmount(Math.abs(declaration.amount) - totalAssignedAbs);
    }
  }, [declaration.transactionType, declaration.memberValues, declaration.amount]);

  // Handle member value change (clamped, sets member to manual)
  const handleValueChange = (memberId: number, value: number) => {
    setDeclaration((prev) => {
      const valueInCentsAbs = Math.max(0, Math.floor((value || 0) * 100));
      const memberRations = { ...(prev.memberRations ?? {}) };
      memberRations[memberId] = undefined;

      const pre = recalcFromRations(prev, { memberRations });
      const otherMembersTotalAbs = Object.entries(pre.memberValues)
        .filter(([id]) => +id !== memberId)
        .reduce((sum, [, v]) => sum + Math.abs(v || 0), 0);
      const maxForThisMemberAbs = Math.max(0, Math.abs(prev.amount) - otherMembersTotalAbs);
      const clampedAbs = Math.min(maxForThisMemberAbs, valueInCentsAbs);

      const updatedMemberValues = {
        ...pre.memberValues,
        [memberId]: clampedAbs,
      };

      return recalcFromRations(
        { ...pre, memberValues: updatedMemberValues },
        {
          memberValues: updatedMemberValues,
          memberRations,
        },
      );
    });
  };

  const handleAmountChange = (valueEuros: number) => {
    setDeclaration((prevTns) => ({
      ...prevTns,
      transactionType: undefined,
      amount: valueEuros ? -Math.abs(Math.floor(valueEuros * 100)) : NaN,
    }));
  };

  const splitEqually = () => {
    setDeclaration((prev) => {
      const selected = connections.map((m) => +m.dashboardMemberId);
      if (selected.length === 0) return prev;
      const totalAbs = Math.abs(prev.amount);
      const equalAbs = Math.floor(totalAbs / selected.length);
      const remainder = totalAbs % selected.length;
      const memberValues: Record<number, number> = {};
      const memberRations: Record<number, number | undefined> = {};
      selected.forEach((id, index) => {
        memberValues[id] = equalAbs + (index < remainder ? 1 : 0);
        memberRations[id] = undefined;
      });
      return { ...prev, selectedMembers: selected, memberValues, memberRations };
    });
  };

  const resetSplit = () => {
    setDeclaration((prev) => ({
      ...prev,
      selectedMembers: [],
      memberValues: {},
      memberRations: {},
    }));
  };

  const isResetable = useMemo(
    () => Object.values(declaration.memberValues).some((v) => (v || 0) > 0),
    [declaration.memberValues],
  );

  const handleSave = () => {
    let memberSplits: BankTransactionParticipation[] | undefined;
    if (declaration.transactionType?.startsWith('PERSONAL')) {
      const sign = declaration.amount >= 0 ? 1 : -1;
      const participatingMembers = Object.entries(declaration.memberValues)
        .filter(([, value]) => (value || 0) > 0)
        .map(([id]) => +id);
      if (participatingMembers.length === 0) return;
      memberSplits = participatingMembers.map((memberId) => ({
        dashboardMemberId: memberId,
        amount: sign * (declaration.memberValues[memberId] || 0),
      }));
    }

    if (!declaration.transactionType || Number.isNaN(declaration.amount)) return;
    if (!declaration.payerId) return;
    if (
      declaration.transactionType === TransactionType.CONTRIBUTION &&
      (!declaration.contributionPayment ||
        !declaration.contributionPayment.dashboardMemberId ||
        !declaration.contributionPayment.contributionPlanId)
    )
      return;

    const declarationDto: CreateBankTransactionModel = {
      amount: declaration.amount,
      description: declaration.description,
      date: declaration.date,
      mutationId: declaration.mutationId,
      payerId: declaration.payerId,
      transactionType: declaration.transactionType,
      participations: declaration.transactionType.startsWith('PERSONAL') ? memberSplits : undefined,
      contributionPayment:
        declaration.transactionType === TransactionType.CONTRIBUTION
          ? {
            contributionPlanId: declaration.contributionPayment!.contributionPlanId!,
            dashboardMemberId: declaration.contributionPayment!.dashboardMemberId!,
          }
          : undefined,
    };

    // Caller should convert to CreateJournalEntryDto (transactionToJournalEntryDto + ledger accounts)
    // and call createJournalEntry/updateJournalEntry so the entry has the correct sourceType
    // (e.g. GROUP_EXPENSE for declarations, PERSONAL_EXPENSE for split personal).
    onSplitSuccess?.(declarationDto);
  };

  // No checkbox selection; SplitMembersList manages values/rations

  if (!declaration) {
    return null;
  }

  return (
    <ModalLayout open={open} setOpen={setOpen} className="w-full max-w-2xl rounded-lg" closeButton>
      <div className="relative flex max-h-[calc(100vh-5rem)] flex-col overflow-hidden rounded-md bg-secondary-50">
        <div className="flex-1 overflow-y-auto p-4">
          {/* Header */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold">
              {editMode ? t('editDeclaration') : t('addDeclaration')}
            </h3>
          </div>

          {/* Processor Information */}
          {/* Note: DeclarationModal uses legacy BankTransaction model, processor info not available */}

          {/* Details card */}
          <div className="mb-6 rounded-lg border border-secondary-200 bg-white p-4 shadow-sm">
            <div className="space-y-4">
              <div className="flex flex-row gap-4">
                <div className="flex-1">
                  <Labeled label={t('description')} htmlFor="declaration-description-textarea">
                    <textarea
                      className="h-[unset] w-full"
                      rows={2}
                      id="declaration-description-textarea"
                      placeholder={t('descriptionPlaceholder')}
                      value={declaration.description}
                      onChange={(ev) =>
                        setDeclaration((prevTns) => ({
                          ...prevTns,
                          description: ev.target.value,
                        }))
                      }
                    />
                  </Labeled>
                  <Labeled label={t('date')}>
                    <DatePicker
                      disabled={false}
                      className="h-[unset] w-full"
                      date={declaration.date}
                      includeTime={false}
                      setDate={(newDate) =>
                        setDeclaration((prevTns) => ({
                          ...prevTns,
                          date: newDate ?? new Date(),
                        }))
                      }
                    />
                    {declaration.date && <p className="pb-1 text-sm text-opacity-80">{formatDate(declaration.date)}</p>}
                  </Labeled>
                </div>
                <Labeled label={t('amount')} className="flex max-w-36 flex-1">
                  <CurrencyInput
                    type="text"
                    className="w-full"
                    value={
                      Number.isNaN(declaration.amount) || declaration.amount === 0
                        ? ''
                        : String(Math.abs(declaration.amount) / 100)
                    }
                    handleChange={handleAmountChange}
                    error={Number.isNaN(declaration.amount) ? t('invalidAmount') : undefined}
                  />
                </Labeled>
              </div>

              <Labeled label={t('paidBy')}>
                <DropdownSelect
                  options={connections.map((c) => ({
                    label: `${c.fields.FIRST_NAME} ${c.fields.LAST_NAME}`,
                    value: +c.dashboardMemberId,
                  }))}
                  setValue={(payerId) =>
                    setDeclaration((prevTns) => ({
                      ...prevTns,
                      payerId,
                    }))
                  }
                  value={declaration.payerId}
                />
              </Labeled>
            </div>
          </div>

          {/* Type selection card */}
          <div className="mb-6 rounded-lg border border-secondary-200 bg-white p-4 shadow-sm">
            <Slider
              label={t('assignmentTypeLabel', { type: 'expense' })}
              items={[
                {
                  label: tEnum('TransactionType.GROUP_EXPENSE'),
                  value: TransactionType.GROUP_EXPENSE,
                },
                {
                  label: tEnum('TransactionType.PERSONAL_EXPENSE'),
                  value: TransactionType.PERSONAL_EXPENSE,
                },
              ]}
              value={declaration.transactionType}
              onChange={(tt) =>
                setDeclaration((prevTns) => ({
                  ...prevTns,
                  transactionType: tt,
                }))
              }
            />
          </div>

          {declaration.transactionType?.startsWith('PERSONAL') && (
            <div className="mb-6">
              <SplitMembersList
                members={connections.map((m) => ({
                  id: +m.dashboardMemberId,
                  label: `${m.fields.FIRST_NAME} ${m.fields.LAST_NAME}`,
                }))}
                memberValues={declaration.memberValues}
                memberRations={declaration.memberRations}
                onSetValue={(id, v) => handleValueChange(id, v)}
                onSetRations={(id, r) =>
                  setDeclaration((prev) =>
                    recalcFromRations(prev, {
                      memberRations: { ...(prev.memberRations ?? {}), [id]: r },
                    }),
                  )
                }
                headerTitle={t('selectMembers')}
                onSplit={splitEqually}
                onClear={resetSplit}
                isResetable={isResetable}
                remainingAmount={remainingAmount}
                remainingLabel={t('remaining')}
              />
            </div>
          )}
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 z-10 border-t border-secondary-200 bg-secondary-50 p-4 shadow-lg">
          <div className="flex justify-end gap-2">
            <Button
              variant="primary-dark"
              className="bg-secondary-200 text-primary-900"
              onClick={() => setOpen(false)}>
              {t('cancelButton')}
            </Button>
            <Button
              variant="primary-dark"
              onClick={handleSave}
              disabled={
                !declaration.transactionType ||
                remainingAmount !== 0 ||
                !declaration.amount ||
                !declaration.payerId
              }>
              {t('assignButton')}
            </Button>
          </div>
        </div>
      </div>
    </ModalLayout>
  );
}
